import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api.service';

/**
 * Custom hook for fetching message statistics
 */
export const useMessageStats = ({ dateRange, type = 'daily' }) => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const fetchStats = useCallback(async () => {
		if (!dateRange?.startDate || !dateRange?.endDate) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const params = {
				startDate: dateRange.startDate,
				endDate: dateRange.endDate
			};

			let response;
			switch (type) {
				case 'daily':
					response = await apiService.messages.getDailyStats(params);
					break;
				case 'hourly':
					response = await apiService.messages.getHourlyStats(params);
					break;
				case 'status':
					response = await apiService.messages.getStatusStats(params);
					break;
				default:
					throw new Error(`Unknown stats type: ${type}`);
			}

			setData(response.data);
		} catch (err) {
			setError(err.message || 'Failed to fetch statistics');
			console.error(`Error fetching ${type} stats:`, err);
		} finally {
			setLoading(false);
		}
	}, [dateRange, type]);

	useEffect(() => {
		fetchStats();
	}, [fetchStats]);

	return { data, loading, error, refetch: fetchStats };
};

/**
 * Custom hook for fetching broadcast statistics
 */
export const useBroadcastStats = ({ dateRange }) => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const fetchStats = useCallback(async () => {
		if (!dateRange?.startDate || !dateRange?.endDate) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const response = await apiService.broadcasts.getStatusStats({
				startDate: dateRange.startDate,
				endDate: dateRange.endDate
			});

			setData(response.data);
		} catch (err) {
			setError(err.message || 'Failed to fetch broadcast statistics');
			console.error('Error fetching broadcast stats:', err);
		} finally {
			setLoading(false);
		}
	}, [dateRange]);

	useEffect(() => {
		fetchStats();
	}, [fetchStats]);

	return { data, loading, error, refetch: fetchStats };
};
